import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SideBar from "../Components/SideBar.js";
import VisitDetailsMobile from "./VisitDetails_mobile.js";
import { API_BASE_URL } from "../config.js";
import "./VisitDetails.css";
import html2pdf from "html2pdf.js";

export default function VisitDetails() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const location = useLocation();
  const role = location.state?.role || "doctor";
  const passedPatient = location.state?.patient; // استقبال بيانات المريض من الواجهة السابقة
  
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const pdfRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchVisitDetails = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        // 1. جلب تفاصيل الزيارة
        const visitRes = await fetch(`${API_BASE_URL}/visits/${id}/`, { method: "GET", headers });
        if (!visitRes.ok) throw new Error("فشل في جلب تفاصيل الزيارة");
        const visitData = await visitRes.json();

        // 2. التحقق من بيانات المريض (نجلبها إن لم تكن ممررة)
        let patientData = passedPatient;
        if (!patientData && visitData.patient) {
          const patientRes = await fetch(`${API_BASE_URL}/patients/${visitData.patient}/`, { method: "GET", headers });
          if (patientRes.ok) patientData = await patientRes.json();
        }

        // 3. جلب بيانات المشفى من خلال بيانات الطبيب
        let hospitalData = null;
        let doctoeData = null;
        if (visitData.doctor) {
          const doctorRes = await fetch(`${API_BASE_URL}/doctors/${visitData.doctor}/`, { method: "GET", headers });
          if (doctorRes.ok) {
            const doctorData = await doctorRes.json();
            if (doctorData.hospital) {
              const hospRes = await fetch(`${API_BASE_URL}/hospitals/${doctorData.hospital}/`, { method: "GET", headers });
              if (hospRes.ok) 
                {hospitalData = await hospRes.json();
                  doctoeData = doctorData.real_name;
                }
            }
          } 
        }
        let diseaseData = null;
        if (visitData.disease) {
          const diseaseRes = await fetch(`${API_BASE_URL}/diseases/${visitData.disease}/`, { method: "GET", headers });
          if (diseaseRes.ok) diseaseData = await diseaseRes.json();
        }

        // 4. دمج كافة البيانات
        setVisit({
          ...visitData,
          patient_details: patientData,
          hospital_details: hospitalData,
          doctor_name: doctoeData,
          disease_details: diseaseData,
        });
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchVisitDetails();
  }, [id, passedPatient]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    const element = pdfRef.current;
    if (!element) return;
    const opt = {
      margin: 10,
      filename: `تقرير_طبي_زيارة_${visit.id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  if (loading) {
    return (
      <div className="visit-details-container" dir="rtl">
        <SideBar role={role} />
        <h2 style={{ padding: "50px" }}>جاري التحميل...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="visit-details-container" dir="rtl">
        <SideBar role={role} />
        <h2 style={{ padding: "50px", color: "red" }}>{error}</h2>
      </div>
    );
  }

  if (!visit) return null;

  if (isMobile) {
    return <VisitDetailsMobile 
              visit={visit} 
              onBack={() => navigate(-1)} 
              onPrint={handlePrint} 
              onDownloadPDF={handleDownloadPDF} 
              pdfRef={pdfRef}
           />;
  }

  return (
    <div className="visit-details-container" dir="rtl">
      <div className="no-print-sidebar">
        <SideBar role={role} />
      </div>
      <div className="visit-details-content">
        <div className="visit-actions-bar no-print">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <i className="fa-solid fa-arrow-right"></i> رجوع
          </button>
          <div className="action-buttons">
            <button className="print-btn" onClick={handlePrint}>
              <i className="fa-solid fa-print"></i> طباعة
            </button>
            <button className="pdf-btn" onClick={handleDownloadPDF}>
              <i className="fa-solid fa-file-pdf"></i> تصدير PDF
            </button>
          </div>
        </div>

       <div className="medical-report-paper" ref={pdfRef} dir="rtl" style={{ textAlign: "right" }}>
          <div className="report-header-print">
            <div className="hospital-info">
              <h2>{visit.hospital_details?.name || "مشفى غير معروف"}</h2>
              <p>التاريخ: {new Date().toLocaleDateString('ar-EG')}</p>
            </div>
            <div className="report-logo">
              <i className="fa-solid fa-heart-pulse fa-3x" style={{ color: "#2c3e50" }}></i>
            </div>
          </div>
          <hr className="report-divider" />
          <h1 className="report-title">تقرير طبي للحالة (#{visit.id})</h1>
          
          <div className="report-grid">
            <div className="report-section full-width">
              <h3><i className="fa-solid fa-user"></i> تفاصيل المريض</h3>
              <div className="info-fields">
                <p><strong>اسم المريض:</strong> {visit.patient_name || visit.patient_details?.name || "غير متوفر"}</p>
                <p><strong>الرقم الوطني:</strong> {visit.patient_national_number || visit.patient_details?.national_number || "غير متوفر"}</p>
                <p><strong>الجنس:</strong> {visit.patient_details?.gender === "male" ? "ذكر" : visit.patient_details?.gender === "female" ? "أنثى" : visit.patient_details?.gender || "غير متوفر"}</p>
                <p><strong>تاريخ الميلاد:</strong> {visit.patient_details?.birth_date || "غير متوفر"}</p>
              </div>
            </div>

            <div className="report-section">
              <h3><i className="fa-solid fa-stethoscope"></i> تفاصيل التشخيص</h3>
              <div className="info-fields">
                <p><strong>المرض:</strong> {visit.disease_name || visit.disease_details?.name || "غير متوفر"}</p>
                <p><strong>الرمز (Code):</strong> {visit.disease_code || visit.disease_details?.disease_code || "غير متوفر"}</p>
                <p><strong>تاريخ التشخيص:</strong> {visit.diagnosis_date || "غير متوفر"}</p>
                <p><strong>الحالة:</strong> {visit.status === 'infected' ? 'مصاب' : visit.status === 'cured' ? 'متعافي' : visit.status || "غير متوفر"}</p>
              </div>
            </div>

            <div className="report-section">
              <h3><i className="fa-solid fa-user-md"></i> معلومات الطبيب</h3>
              <div className="info-fields">
                <p><strong>الدكتور المعالج:</strong> {visit.doctor_name || "غير متوفر"}</p>
                <p><strong>المشفى:</strong> {visit.hospital_details?.name || "غير متوفر"}</p>
                <p><strong>الحالة الاجتماعية:</strong> {visit.marital_status || "غير متوفر"}</p>
              </div>
            </div>

            {visit.geodata && (
              <div className="report-section full-width">
                <h3><i className="fa-solid fa-map-location-dot"></i> الموقع الجغرافي للمرض</h3>
                <div className="info-fields geo-fields">
                  <p><strong>خط العرض (Latitude):</strong> {visit.geodata.latitude}</p>
                  <p><strong>خط الطول (Longitude):</strong> {visit.geodata.longitude}</p>
                  <p><strong>مكان الإصابة:</strong> {visit.geodata.region_type || "غير متوفر"}</p>
                </div>
              </div>
            )}

            <div className="report-section full-width text-block">
              <h3><i className="fa-solid fa-file-medical"></i> التقرير الطبي (Diagnose)</h3>
              <p className="diagnose-text">
                {visit.diagnose || "لا يوجد تفاصيل إضافية للتشخيص."}
              </p>
            </div>
          </div>

          <div className="report-footer-print">
            <div className="signature-block">
              <p>توقيع الطبيب:</p>
              <div className="signature-line"></div>
            </div>
            <div className="date-block">
              <p>حرر في: {new Date().toLocaleDateString('ar-EG')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}